import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, canViewActivityLogs } from '../../utils/auth';
import { getEditRequests, updateEditRequestStatus, addLog } from '../../utils/storage';
import { getDeviceInfo, getIPAddress } from '../../utils/device';
import type { EditRequest } from '../../types';
import DashboardLayout from '../../components/feature/DashboardLayout';

const getVitalLabel = (type: string) => {
  const labels: Record<string, string> = {
    bp: 'Blood Pressure',
    heart_rate: 'Heart Rate',
    spo2: 'SpO2',
    temperature: 'Temperature',
    rbs: 'Random Blood Sugar',
    cholesterol: 'Cholesterol',
    height_weight: 'Height & Weight',
    body_fat: 'Body Fat',
    blood_grouping: 'Blood Grouping',
    colour_blindness: 'Colour Blindness',
    visual_acuity: 'Visual Acuity',
    ecg: 'ECG',
  };
  return labels[type] || type;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
};

const getStatusIcon = (status: string) => {
  const icons: Record<string, string> = {
    pending: 'ri-time-line',
    approved: 'ri-check-line',
    rejected: 'ri-close-line',
  };
  return icons[status] || 'ri-question-line';
};

export default function EditRequests() {
  const navigate = useNavigate();
  const user = getUser();
  const [requests, setRequests] = useState<EditRequest[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; requestId: string; action: 'approve' | 'reject' }>({ show: false, requestId: '', action: 'approve' });
  const [successMsg, setSuccessMsg] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!user || !canViewActivityLogs(user)) {
      navigate('/dashboard');
      return;
    }
    loadRequests();

    // Poll for new requests every 5 seconds
    const interval = setInterval(() => {
      loadRequests();
    }, 5000);

    return () => clearInterval(interval);
  }, [user, navigate]);

  const loadRequests = async () => {
    const allRequests = await getEditRequests();
    setRequests(allRequests);
  };

  const handleAction = async (requestId: string, action: 'approve' | 'reject') => {
    setActionLoading(true);
    try {
      const status = action === 'approve' ? 'approved' : 'rejected';
      await updateEditRequestStatus(requestId, status, user?.username || '');

      const { browser, device } = getDeviceInfo();
      const ipAddress = await getIPAddress();

      const request = requests.find(r => r.id === requestId);

      await addLog({
        id: Date.now().toString(),
        userId: user?.id || '',
        username: user?.username || '',
        action: 'Edit Request',
        details: `${status.charAt(0).toUpperCase() + status.slice(1)} edit request ${requestId}${request ? ` for ${getVitalLabel(request.vitalType)} of patient ${request.patientName}` : ''}${action === 'approve' && request ? `. Value changed from "${request.currentValue}" to "${request.newValue}"` : ''}`,
        ipAddress,
        deviceName: device,
        browser,
        timestamp: new Date().toISOString(),
      });

      setConfirmModal({ show: false, requestId: '', action: 'approve' });
      setSuccessMsg(action === 'approve' ? 'Edit request approved. The vital record has been updated.' : 'Edit request rejected.');
      await loadRequests();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('Error handling action:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredRequests = requests.filter((req) => {
    if (filter === 'all') return true;
    return req.status === filter;
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Dashboard
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Edit Requests</h2>
                <p className="text-sm text-gray-500 mt-1">Review and manage requests to edit vital records</p>
              </div>
              {pendingCount > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full whitespace-nowrap">
                  <i className="ri-time-line"></i>
                  {pendingCount} Pending
                </span>
              )}
            </div>

            {successMsg && (
              <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                <i className="ri-check-line"></i>
                {successMsg}
              </div>
            )}

            <div className="mb-6">
              <div className="flex gap-2 flex-wrap">
                {(['all', 'pending', 'approved', 'rejected'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      filter === f
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                    {f === 'pending' && pendingCount > 0 && (
                      <span className="ml-1.5 bg-white/30 text-white px-1.5 py-0.5 rounded-full text-xs">
                        {pendingCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {filteredRequests.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-file-list-3-line text-4xl text-gray-300"></i>
                  </div>
                  <p className="text-sm text-gray-500">No {filter !== 'all' ? filter : ''} edit requests found</p>
                </div>
              ) : (
                filteredRequests.map((request) => (
                  <div
                    key={request.id}
                    className="border border-gray-200 rounded-lg p-5 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                            <i className={getStatusIcon(request.status)}></i>
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </span>
                          <span className="text-xs text-gray-400">ID: {request.id}</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Patient:</span>
                            <span className="ml-2 font-medium text-gray-800">{request.patientName || request.patientId}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Vital Type:</span>
                            <span className="ml-2 font-medium text-gray-800">{getVitalLabel(request.vitalType || '')}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Current Value:</span>
                            <span className="ml-2 font-medium text-red-600">{request.currentValue}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">New Value:</span>
                            <span className="ml-2 font-medium text-green-600">{request.newValue}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Requested by:</span>
                            <span className="ml-2 font-medium text-gray-800">{request.requestedBy}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Requested at:</span>
                            <span className="ml-2 font-medium text-gray-800">{formatDate(request.requestedAt)}</span>
                          </div>
                        </div>

                        <div className="mt-3 bg-gray-50 rounded-lg p-3">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reason</span>
                          <p className="text-sm text-gray-700 mt-1">{request.reason}</p>
                        </div>

                        {request.reviewedBy && (
                          <div className="mt-3 text-xs text-gray-500">
                            {request.status === 'approved' ? 'Approved' : 'Rejected'} by <strong>{request.reviewedBy}</strong>
                            {request.reviewedAt && ` on ${formatDate(request.reviewedAt)}`}
                          </div>
                        )}
                      </div>

                      {request.status === 'pending' && (
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <button
                            onClick={() => setConfirmModal({ show: true, requestId: request.id, action: 'approve' })}
                            className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-check-line"></i>
                            Approve
                          </button>
                          <button
                            onClick={() => setConfirmModal({ show: true, requestId: request.id, action: 'reject' })}
                            className="flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-close-line"></i>
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {confirmModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <div className="text-center mb-5">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${confirmModal.action === 'approve' ? 'bg-green-100' : 'bg-red-100'}`}>
                <i className={`${confirmModal.action === 'approve' ? 'ri-check-line text-green-600' : 'ri-close-line text-red-600'} text-2xl`}></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800">
                {confirmModal.action === 'approve' ? 'Approve Edit Request?' : 'Reject Edit Request?'}
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                {confirmModal.action === 'approve'
                  ? 'This will update the vital record with the new value.'
                  : 'This will reject the edit request. The vital record will remain unchanged.'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => handleAction(confirmModal.requestId, confirmModal.action)}
                disabled={actionLoading}
                className={`flex-1 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-70 ${
                  confirmModal.action === 'approve'
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {actionLoading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Processing...</>
                ) : (
                  confirmModal.action === 'approve' ? 'Yes, Approve' : 'Yes, Reject'
                )}
              </button>
              <button
                onClick={() => setConfirmModal({ show: false, requestId: '', action: 'approve' })}
                disabled={actionLoading}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}